import {
  GatewayConformite,
  GatewayEntreprise,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import { activitesFromEntreprise } from "@endya-conformite/ui-kit";
import RightArrow from "@endya-conformite/ui-kit/src/assets/icons/arrow-right.svg?react";
import Report from "@endya-conformite/ui-kit/src/assets/icons/report.svg?react";
import {
  categorieToAvancementKey,
  getAxesConformiteByActivites,
} from "@src/societes/societe.helper";
import { useEntrepriseAvancement } from "@src/societes/useEntrepriseAvancement";
import { useMemo } from "react";
import { ValidesData } from "./ValidesData";

const useEntrepriseAxesConformite = (
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
) => {
  const { getEntrepriseAvancementForAssujettiAxe } = useEntrepriseAvancement();

  return useMemo<Record<GatewayConformite.AxeConformite, number>>(() => {
    const avancementByAxeConformite: Record<
      GatewayConformite.AxeConformite,
      number
    > = {
      [GatewayConformite.AxeConformite.HONORABILITE]:
        getEntrepriseAvancementForAssujettiAxe(entreprise, "honorabilite"),
      [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IAS]:
        getEntrepriseAvancementForAssujettiAxe(
          entreprise,
          "formationContinueIas"
        ),
      [GatewayConformite.AxeConformite.FORMATION_CONTINUE_IOBSP]:
        getEntrepriseAvancementForAssujettiAxe(
          entreprise,
          "formationContinueIobsp"
        ),
      [GatewayConformite.AxeConformite.CAPACITE_PRO_IAS]:
        getEntrepriseAvancementForAssujettiAxe(
          entreprise,
          "capaciteProfessionnelleIas"
        ),
      [GatewayConformite.AxeConformite.CAPACITE_PRO_IOBSP]:
        getEntrepriseAvancementForAssujettiAxe(
          entreprise,
          "capaciteProfessionnelleIobsp"
        ),
      [GatewayConformite.AxeConformite.RC_PRO_COA]:
        (entreprise.avancement[
          categorieToAvancementKey[GatewayInfoSocieteOrias.Categorie.COA]
        ] as number) ?? 0,
      [GatewayConformite.AxeConformite.RC_PRO_MIA]:
        (entreprise.avancement[
          categorieToAvancementKey[GatewayInfoSocieteOrias.Categorie.MIA]
        ] as number) ?? 0,
      [GatewayConformite.AxeConformite.RC_PRO_COBSP]:
        (entreprise.avancement[
          categorieToAvancementKey[GatewayInfoSocieteOrias.Categorie.COBSP]
        ] as number) ?? 0,
      [GatewayConformite.AxeConformite.RC_PRO_MIOBSP]:
        (entreprise.avancement[
          categorieToAvancementKey[GatewayInfoSocieteOrias.Categorie.MIOBSP]
        ] as number) ?? 0,
      [GatewayConformite.AxeConformite.GARANTIE_FINANCIERE_IAS]:
        entreprise.avancement.garantieFinanciereIas ?? 0,
      [GatewayConformite.AxeConformite.GARANTIE_FINANCIERE_IOBSP]:
        entreprise.avancement.garantieFinanciereIobsp ?? 0,
      [GatewayConformite.AxeConformite.DECLARATION_ACTIVITE_IAS]:
        entreprise.avancement.declarationActiviteIas ?? 0,
      [GatewayConformite.AxeConformite.DECLARATION_ACTIVITE_IOBSP]:
        entreprise.avancement.declarationActiviteIobsp ?? 0,
    };

    const axes = getAxesConformiteByActivites(
      activitesFromEntreprise(entreprise)
    );
    const avancementByAxeEntries = Object.entries(
      avancementByAxeConformite
    ) as [GatewayConformite.AxeConformite, number][];
    const filteredAvancementByAxe = avancementByAxeEntries.filter(([axeName]) =>
      axes.includes(axeName)
    );

    return Object.fromEntries(filteredAvancementByAxe) as Record<
      GatewayConformite.AxeConformite,
      number
    >;
  }, [entreprise, getEntrepriseAvancementForAssujettiAxe]);
};

type EntrepriseAxesConformiteProps = {
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
};
export function EntrepriseAxesConformite({
  entreprise,
}: EntrepriseAxesConformiteProps) {
  const axesConformite = useEntrepriseAxesConformite(entreprise);

  return (
    <ValidesData
      title="Axes conformes"
      titleIcon={<Report />}
      icon={<RightArrow />}
      linkLabel="Renseignez votre conformité"
      values={Object.values(axesConformite)}
      getAvancement={(avancement) => avancement}
    />
  );
}
